import { computed, Signal, signal } from '@angular/core';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from 'src/app/common/constants-ejb';

export type MensajeTipoBusqueda = 'info' | 'warning' | 'error';

interface TableStyleConfig {
  tableClassName: string;
  striped?: boolean;
  gridlines?: boolean;
}

interface TableSelectionConfig {
  mode?: string;
}

interface TableConfigLike {
  style: TableStyleConfig;
  selection: TableSelectionConfig;
}

function normalizeSearchErrorMessage(candidate: unknown): string | null {
  if (typeof candidate !== 'string') {
    return null;
  }
  const trimmed = candidate.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.toLowerCase() === 'validation.error') {
    return null;
  }
  return trimmed;
}

function extractSearchErrorMessage(payload: unknown): string | null {
  if (payload == null) {
    return null;
  }

  if (typeof payload === 'string') {
    const trimmed = payload.trim();
    if (!trimmed) {
      return null;
    }

    try {
      return extractSearchErrorMessage(JSON.parse(trimmed));
    } catch {
      return normalizeSearchErrorMessage(trimmed);
    }
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const message = extractSearchErrorMessage(item);
      if (message) {
        return message;
      }
    }
    return null;
  }

  if (typeof payload === 'object') {
    const body = payload as Record<string, unknown>;
    const candidates = [
      body['detail'],
      body['message'],
      body['mensaje'],
      body['error_description'],
      body['error']
    ];

    for (const candidate of candidates) {
      const message = extractSearchErrorMessage(candidate);
      if (message) {
        return message;
      }
    }
  }

  return null;
}

export function resolveSearchErrorMessage(
  error: unknown,
  fallbackMessage: string
): string {
  const errorObject =
    typeof error === 'object' && error !== null
      ? (error as Record<string, unknown>)
      : null;

  const fromErrorBody = extractSearchErrorMessage(errorObject?.['error']);
  if (fromErrorBody) {
    return fromErrorBody;
  }

  if (error instanceof Error) {
    return fallbackMessage;
  }

  const fromRoot = extractSearchErrorMessage(errorObject);
  return fromRoot ?? fallbackMessage;
}

/**
 * Base reutilizable para dialogos de busqueda con paginacion.
 */
export abstract class BaseBusquedaPaginadaDialog<TItem> {
  items = signal<TItem[]>([]);
  loading = signal(false);
  mensaje = signal('');
  mensajeTipo = signal<MensajeTipoBusqueda>('info');
  busquedaRealizada = false;

  totalRegistros = signal(0);
  paginaActual = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  readonly totalPaginas = computed(() => {
    const total = this.totalRegistros();
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  // Derivados de paginaActual/totalPaginas — nunca se setean a mano, para que
  // no puedan quedar desincronizados de la página real tras un llamado async.
  readonly hasPrevious = computed(() => this.paginaActual() > 1);
  readonly hasNext = computed(() => this.paginaActual() < this.totalPaginas());

  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.paginaActual() - 1) * this.pageSize() + 1;
  });

  readonly registroFin = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return Math.min(this.paginaActual() * this.pageSize(), this.totalRegistros());
  });

  // Backward-compatible aliases used by existing components/specs.
  rowsPerPage = this.pageSize;
  registroDesde = this.registroInicio;
  registroHasta = this.registroFin;

  protected abstract ejecutarBusqueda(start: number): Promise<void>;

  protected shouldTriggerSearchOnPageSizeChange(): boolean {
    return true;
  }

  async onPageSizeChange(): Promise<void> {
    this.pageSize.set(this.selectedPageSize);
    this.paginaActual.set(1);
    if (!this.shouldTriggerSearchOnPageSizeChange()) {
      return;
    }
    await this.ejecutarBusqueda(0);
  }

  async onSelectedPageSizeChange(size: number): Promise<void> {
    this.selectedPageSize = size;
    await this.onPageSizeChange();
  }

  async irPrimeraPagina(): Promise<void> {
    this.paginaActual.set(1);
    await this.ejecutarBusqueda(0);
  }

  async loadPreviousPage(): Promise<void> {
    if (this.paginaActual() > 1) {
      const newStart = (this.paginaActual() - 2) * this.pageSize();
      await this.ejecutarBusqueda(newStart);
    }
  }

  async loadNextPage(): Promise<void> {
    if (this.paginaActual() < this.totalPaginas()) {
      const newStart = this.paginaActual() * this.pageSize();
      await this.ejecutarBusqueda(newStart);
    }
  }

  async irUltimaPagina(): Promise<void> {
    const lastStart = (this.totalPaginas() - 1) * this.pageSize();
    await this.ejecutarBusqueda(lastStart);
  }

  protected resetPaginacion(): void {
    this.totalRegistros.set(0);
    this.paginaActual.set(1);
    this.pageSize.set(DEFAULT_ROWS_PER_PAGE);
    this.selectedPageSize = DEFAULT_ROWS_PER_PAGE;
  }

  protected clearMensajes(): void {
    this.mensaje.set('');
    this.mensajeTipo.set('info');
  }

  protected resolverMensajeErrorBusqueda(
    error: unknown,
    fallbackMessage: string
  ): string {
    return resolveSearchErrorMessage(error, fallbackMessage);
  }

  protected buildTableStyleClass(
    tableConfigSignal: Signal<TableConfigLike>
  ): Signal<string> {
    return computed(() => {
      const config = tableConfigSignal();
      const classes = ['p-datatable-sm', config.style.tableClassName];
      if (config.style.gridlines !== false) {
        classes.push('p-datatable-gridlines');
      }
      if (config.style.striped !== false) {
        classes.push('p-datatable-striped');
      }
      return classes.join(' ');
    });
  }

  protected buildTableSelectionMode(
    tableConfigSignal: Signal<TableConfigLike>
  ): Signal<'single' | 'multiple' | undefined> {
    return computed(() => {
      const mode = tableConfigSignal().selection.mode;
      if (mode === 'single' || mode === 'multiple') {
        return mode;
      }
      return undefined;
    });
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }
}